import axiosInstance from "../../../infrastructure/helpers/axiosHelper";
import { keepaliveAdapter } from "../../../infrastructure/helpers/keepaliveAdapter";
import { endpointsMetadata } from "../../../infrastructure/routes/endpointsMetadata";

// The push that rings when a rest ends while the phone is locked: asked for as the app leaves the
// screen, cancelled when it comes back. Both calls happen while the page is being hidden or just
// woke up, so they use the keepalive adapter and never throw into the timer.

export interface IScheduleRestTimerAlarmArgs {
  /** This device's push endpoint. */
  endpoint: string;
  remainingMs: number;
  exerciseName: string;
  sequence: number;
}

export interface ICancelRestTimerAlarmArgs {
  endpoint: string;
  sequence: number;
}

let lastSequence = 0;

/**
 * Numbers this device's alarm requests, so the server can tell which of two requests that
 * overtook each other is the newer one. Taken from the clock, so a reloaded page still
 * outranks what it sent before the reload.
 */
export const nextAlarmSequence = (): number => {
  lastSequence = Math.max(Date.now(), lastSequence + 1);
  return lastSequence;
};

const readScheduled = (data: unknown): boolean => {
  try {
    const parsed = typeof data === "string" ? JSON.parse(data) : data;
    return (parsed as { scheduled?: boolean } | null)?.scheduled === true;
  } catch {
    return false;
  }
};

/** True when the server will push to this device; false means the page has to ring by itself. */
export const scheduleRestTimerAlarm = async ({
  endpoint,
  remainingMs,
  exerciseName,
  sequence,
}: IScheduleRestTimerAlarmArgs): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(
      endpointsMetadata.restTimerAlarm.schedule,
      { endpoint, remainingMs: Math.round(remainingMs), exerciseName, sequence },
      { adapter: keepaliveAdapter },
    );
    return readScheduled(response.data);
  } catch {
    // Flag off (404), offline, signed out: nobody will push, so the page rings if it can.
    return false;
  }
};

export const cancelRestTimerAlarm = async ({
  endpoint,
  sequence,
}: ICancelRestTimerAlarmArgs): Promise<void> => {
  try {
    await axiosInstance.post(
      endpointsMetadata.restTimerAlarm.cancel,
      { endpoint, sequence },
      { adapter: keepaliveAdapter },
    );
  } catch {
    // At worst the push still arrives once; nothing in the timer depends on it.
  }
};
